import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Search,
  ArrowDownUp,
  SlidersHorizontal,
  BellRing,
  EyeClosed,
} from "lucide-react";
import FloatingChat from '@/components/layout/FloatingChat';
import PropertyCard from "@/components/PropertyCard";
import AlertDrawer from "@/components/AlertDrawer";
import FilterDrawer from "@/components/FilterDrawer";
import { SearchBar, SearchFiltersComponent, FilterButton, SearchResults, SearchResultsHeader } from "@/components/search";
import { Header, Footer } from "@/components/layout";
import { useImobiliaria } from '@/hooks/useImobiliaria';
import { useSiteConfig } from '@/hooks/useSiteConfig';
import { useImoveis } from '@/hooks/useImoveis';
import { MOCK_FEATURED_PROPERTIES } from '@/data/mockData';
import type { PropertyCardProps } from '@/components/PropertyCard';
import type { ImoveisFilters } from '@/hooks/useImoveis';

const USE_MOCK_DATA = import.meta.env.VITE_USE_MOCK_DATA === 'true';

// Helper para converter Imovel para PropertyCardProps
const imoveisToPropertyCards = (imoveis: any[]): PropertyCardProps[] => {
  return imoveis.map(imovel => ({
    id: imovel.id,
    title: imovel.titulo || 'Imóvel sem título',
    code: imovel.codigo || '',
    type: imovel.tipo || 'Imóvel',
    attributes: `${imovel.area_total || 0}m² • ${imovel.quartos || 0} Quartos • ${imovel.vagas || 0} vagas`,
    price: imovel.valor_venda 
      ? `R$ ${Number(imovel.valor_venda).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
      : imovel.valor_locacao
      ? `R$ ${Number(imovel.valor_locacao).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
      : 'Sob consulta',
    priceUnit: imovel.valor_locacao ? '/mês' : '',
    additionalCosts: imovel.valor_locacao ? '+ condomínio e IPTU' : '',
    address: `${imovel.endereco || ''}, ${imovel.bairro || ''} - ${imovel.cidade || ''} ${imovel.estado || ''}`,
    image: Array.isArray(imovel.fotos) ? imovel.fotos[0] : imovel.fotos || '/img-default.jpg',
    saleType: imovel.finalidade === 'venda' ? 'Venda' : 'Locação',
    isNew: false,
    engagementCount: imovel.visualizacoes || 0,
  }));
};

export default function SearchPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("Todos");
  const [isAlertDrawerOpen, setIsAlertDrawerOpen] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState<ImoveisFilters>({});

  // Buscar dados da imobiliária
  const { data: imobiliariaData } = useImobiliaria('desordibroker');
  const { data: siteConfigData } = useSiteConfig(imobiliariaData?.id);
  
  // Buscar imóveis com filtros
  const { data: imoveis, isLoading: loadingImoveis } = useImoveis({
    imobiliariaId: imobiliariaData?.id,
    filters,
    limit: 50,
  });

  // Converter imóveis para PropertyCardProps
  const propertyCards: PropertyCardProps[] = imoveis && imoveis.length > 0
    ? imoveisToPropertyCards(imoveis)
    : [];

  

  const handleContactClick = () => {
    const whatsapp = '5519982589820';
    const message = encodeURIComponent('Olá! Gostaria de mais informações.');
    window.open(`https://wa.me/${whatsapp}?text=${message}`, '_blank');
  };

  // Handler para aplicar filtros do drawer
  const handleApplyFilters = (newFilters: ImoveisFilters) => {
    setFilters(newFilters);
    
    // Atualizar tab baseado na finalidade
    if (newFilters.finalidade === 'locacao') {
      setActiveTab('Alugar');
    } else if (newFilters.finalidade === 'venda') {
      setActiveTab('Vender');
    } else {
      setActiveTab('Todos');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Wrapper com largura máxima para monitores ultrawide */}
      <div className="w-full max-w-[1920px] mx-auto">
        {/* Header */}
        <Header 
          onNavigateToProperties={() => navigate('/busca')}
          onNavigateToAbout={() => navigate('/#sobre')}
          onAnnounceClick={() => navigate('/anuncie')}
        />

        {/* Search Bar */}
        <SearchBar
          variant="page"
          showTabs={true}
          onSearch={() => {}}
          className="border-b border-gray-200"
        />

      {/* Main Content */}
      <section className="flex flex-col lg:flex-row w-full items-start min-h-[600px] lg:h-[1045px]">
        {/* Property Listings */}
        <div className="flex w-full lg:w-auto h-full items-start gap-2">
          <div className="flex px-4 lg:px-20 flex-col items-start gap-6 lg:gap-10 w-full">
            {/* Header */}
            <SearchResultsHeader
              totalResults={propertyCards.length}
              location="Campinas, SP"
              showMapButton={false}
            />

            {/* Loading State */}
            {loadingImoveis && (
              <div className="flex justify-center items-center w-full py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neutral-black"></div>
              </div>
            )}

            {/* Empty State */}
            {!loadingImoveis && propertyCards.length === 0 && (
              <div className="flex flex-col items-center justify-center w-full py-20 text-center">
                <p className="text-xl text-neutral-gray-light mb-4">
                  Nenhum imóvel encontrado com os filtros selecionados.
                </p>
                <button
                  onClick={() => setFilters({})}
                  className="px-6 py-3 rounded-full bg-neutral-black text-white hover:bg-neutral-black/90 transition-colors"
                >
                  Limpar filtros
                </button>
              </div>
            )}

            {/* Property Cards Grid */}
            {!loadingImoveis && propertyCards.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 w-full">
                {propertyCards.map((property) => (
                  <div key={property.id} className="w-full lg:w-auto">
                    <PropertyCard
                      {...property}
                      onClick={() => {
                        if (property.code) {
                          navigate(`/imovel/${property.code}`);
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Scrollbar - Only visible on desktop */}
          <div className="hidden lg:flex w-2 h-[1047px] pb-[1014px] flex-col items-center rounded-full bg-gray-200">
            <div className="w-2 h-[33px] rounded-full bg-neutral-dark"></div>
          </div>
        </div>

        {/* Map View - Hidden on mobile, visible on desktop */}
        <div className="hidden lg:block w-[698px] h-[1045px] relative">
          <img
            src="https://api.builder.io/api/v1/image/assets/TEMP/8feb85badb74f189802c5f94c0783948bedf3d30?width=1396"
            alt="Map view"
            className="w-full h-full object-cover"
          />

          {/* Hide Map Button */}
          <button className="absolute top-5 right-6 flex w-[180px] lg:w-[221px] h-[49px] px-6 lg:px-8 py-4 justify-center items-center gap-2 rounded-full bg-white hover:bg-gray-50 transition-colors">
            <span className="text-sm lg:text-base font-normal text-neutral-black">
              Esconder mapa
            </span>
            <EyeClosed className="w-5 lg:w-6 h-5 lg:h-6 text-neutral-black" />
          </button>
        </div>

        {/* Show Map Button - Only visible on mobile */}
        <div className="lg:hidden fixed bottom-20 left-1/2 transform -translate-x-1/2 z-40">
          <button className="flex px-6 py-3 justify-center items-center gap-2 rounded-full bg-neutral-black hover:bg-neutral-black/90 transition-colors text-white">
            <span className="text-sm font-normal">Ver no mapa</span>
          </button>
        </div>
      </section>

      {/* Footer */}
      <Footer onContactClick={handleContactClick} />
      </div>

      <FloatingChat
        position="bottom-right"
        size="lg"
        onClick={handleContactClick}
      />

      {/* Alert Drawer */}
      <AlertDrawer
        isOpen={isAlertDrawerOpen}
        onClose={() => setIsAlertDrawerOpen(false)}
      />

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        currentFilters={filters}
        onApplyFilters={handleApplyFilters}
        availableCount={propertyCards.length}
      />
    </div>
  );
}
